import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { waitForQuery } from "../../api/useQuery.js";
import { checkBackendReady } from "../../api/client.js";
import { getSubjectList } from "../../api/queries.js";
import { useAuth } from "../../auth/AuthProvider.js";
import { BrandScreen } from "./BrandScreen.js";
import { brandIntroRemainingMs } from "./brandIntro.js";
import { type MessageKey, useT } from "@mmm/ui";
const FADE_MS = 400;
const BACKEND_RETRY_MS = 1600;

/**
 * Full-screen brand splash shown on boot, overlaying the app while it loads
 * data underneath. Dismisses (with a fade) only once BOTH the intro animation
 * has fully completed AND the app is ready; under prefers-reduced-motion the
 * assembled logo shows immediately and only readiness gates dismissal.
 *
 * A signed-out visitor is held the same way. The sign-in screen underneath can
 * hand straight off to Entra (the public site's Sign In / Sign Up handoff), so
 * dismissing early here used to cut the logo mid-draw and then leave the page
 * entirely — the intro is only ever seen once per visit, so it gets to finish.
 * The redirect itself waits too, in auth/AuthProvider.tsx.
 */
export function LoadingSplash() {
  const [phase, setPhase] = useState<"showing" | "leaving" | "gone">("showing");
  // State holds the message KEY, translated at render, so a language switch
  // mid-boot still repaints in the new language.
  const [messageKey, setMessageKey] = useState<MessageKey>("splash.loadingWorkspace");
  const { isReady, signedOut } = useAuth();
  const { pathname } = useLocation();
  const t = useT();
  const isPublicLanding = pathname === "/";

  useEffect(() => {
    if (!isReady || isPublicLanding) return;

    let cancelled = false;
    let introDone = false;
    let appReady = false;
    const maybeLeave = () => {
      if (introDone && appReady && !cancelled) setPhase("leaving");
    };

    // Whatever the intro still owes, measured from when it actually started
    // drawing rather than from when MSAL happened to settle.
    const timer = setTimeout(() => {
      introDone = true;
      maybeLeave();
    }, brandIntroRemainingMs());

    if (signedOut) {
      appReady = true;
      maybeLeave();
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const waitForBackend = async () => {
      while (!cancelled) {
        try {
          setMessageKey("splash.connectingBackend");
          await checkBackendReady();
          return;
        } catch {
          setMessageKey("splash.waitingBackend");
          await new Promise((resolve) => setTimeout(resolve, BACKEND_RETRY_MS));
        }
      }
    };

    void (async () => {
      await waitForBackend();
      if (cancelled) return;
      setMessageKey("splash.loadingWorkspace");

      // Warm the subject list used by both the sidebar and the app-level
      // first-subject gate. Page-specific reads wait until the restored page
      // opens instead of delaying startup.
      await Promise.allSettled([waitForQuery(getSubjectList)]);
      appReady = true;
      maybeLeave();
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isPublicLanding, isReady, signedOut]);

  useEffect(() => {
    if (phase !== "leaving") return;
    const timer = setTimeout(() => setPhase("gone"), FADE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (isPublicLanding) return null;
  if (phase === "gone") return null;

  // Before MSAL settles, and for a visitor who turns out to be signed out,
  // the workspace is not what's coming next — say so.
  const activeKey: MessageKey = !isReady || signedOut ? "splash.preparingSignIn" : messageKey;

  return (
    <BrandScreen
      animated
      className={`mmm-splash ${phase === "leaving" ? "mmm-splash--leaving" : ""}`.trim()}
      role="status"
      aria-label={t(activeKey)}
    >
      <div className="mmm-splash-cue">
        <div className="mmm-splash-track">
          <div className="mmm-splash-bar" />
        </div>
        <div className="mmm-splash-message">
          <span>{t(activeKey)}</span>
          <span className="mmm-splash-dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      </div>
    </BrandScreen>
  );
}
